"use client";

import {
  useMemo,
  useState,
  useEffect,
  useRef,
  startTransition,
  useCallback,
  type RefObject,
  type Dispatch,
  type SetStateAction,
} from "react";
import Button from "@/shared/ui/components/button";
import CardForm from "@/shared/ui/components/card-form";
import Modal from "@/shared/ui/components/modal";
import Checkbox from "@/shared/ui/components/checkbox";
import Banner, { BannerType } from "@/shared/ui/components/banner";
import AppWrapper from "@/shared/ui/components/app-wrapper";
import CardMenu, { CardMenuItem } from "@/shared/ui/components/card-menu";
import WalletMenuItem from "@/shared/ui/components/wallet-menu-item";
import { DocumentsModal } from "@/modules/payment-confirmation/components/documents-modal";
import InputText from "@/shared/ui/components/input-text";
import { emptySession } from "@/lib/checkout";
import { parseCardDate } from "@/lib/card";
import * as events from "@/events";
import * as lib3ds from "@/lib/3ds";
import { resolveScheme } from "@/lib/card";
import * as applePay from "@/lib/apple-pay";
import { wait } from "@/lib/common";
import { refetchCheckoutAfterFailed3dsReport } from "@/lib/refetch-after-failed-3ds-report";
import { withCheckoutId, toAppPath } from "@/lib/checkout-id";
import { newLocalStorageStore } from "@/lib/store";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import CardIcon, { CardFlag, cardIconByScheme } from "@/shared/ui/components/card-icon";
import {
  type AppContext,
  type Store,
  CardScheme,
  LOCALIZED_ICON_TYPE,
  CheckoutStatus,
  CHECKOUT_STATUS_PROCESSING_PAYMENT,
  type StoredCard,
  Currency,
  SessionError,
} from "@/types";
import MoneyAmount from "@/shared/ui/components/money-amount";
import {
  useCheckoutSessionV3,
  usePaymentPlansActions,
  useSavedCards,
  useSelectedPaymentMethod,
  useBackFromPaymentMethodSelection,
} from "@/modules/checkout-session";
import {
  useCosmosClient,
  type CosmosClient,
  type TokenizeCardResult,
} from "@/services/cosmos";
import type { PpsPaymentMethodDetails } from "@/modules/checkout-session";
import {
  Page,
  PaymentMethod,
  type CheckoutSession,
  type CardData,
} from "@/types";
interface PaymentConfirmationProps {
  onBack: () => void;
  cartValue?: number;
  onSessionFetched: (session: CheckoutSession) => void;
}
import { NewCardIcon } from "@/shared/ui/icons/new-card";
import LoadingPage from "@/shared/ui/components/loading-page";
import ApplePayModal from "@/shared/ui/components/apple-pay-modal";
import { ErrorScreen } from "@/shared/ui/layout/error-screen";
import { encodeCardScheme } from "@/lib/codec";
import { logError, logInfo, logDebug } from "@/services/logging/logger";

import { useScreenTracking } from "@/shared/hooks/use-screen-tracking";
import i18n from "@/i18n";
import enUSErrors from "@/locale/en_US.errors.json";
import arSAErrors from "@/locale/ar_SA.errors.json";

type PaymentErrorContent = {
  message: string;
};

type PaymentErrorLocale = {
  payment: Record<string, PaymentErrorContent>;
};

const paymentErrorLocales: Record<string, PaymentErrorLocale> = {
  en_US: enUSErrors as PaymentErrorLocale,
  ar_SA: arSAErrors as PaymentErrorLocale,
};

const getPaymentErrorMessage = (code: string | undefined): string => {
  const language = i18n.language?.startsWith("ar") ? "ar_SA" : "en_US";
  const locale = paymentErrorLocales[language] ?? paymentErrorLocales.en_US;
  const paymentErrors = locale.payment;

  if (code && paymentErrors[code]) {
    return paymentErrors[code].message;
  }

  return paymentErrors.UNKNOWN.message;
};
import { useBrowserBackButton } from "@/shared/hooks/use-browser-back-button";
import {
  emitPaymentMethodsShown,
  emitPaymentSubmissionTracking,
  emitPaymentMethodSelectionTracking,
  emitApplePayTracking,
  emitCardFormTracking,
  emitModalTracking,
  emitNavigationTracking,
  emitDocumentTracking,
  emitSlowPaymentLoadingTracking,
} from "@/shared/tracking/analytics";
import { PaymentMethodsView } from "@/modules/payment-methods/views/payment-methods-view";
import {
  handleHttpError,
  type ApiError,
} from "@/lib/http-error-recovery";
import { CksCheckoutStateSchema } from "@/services/cosmos/schema/checkout-service";
import { parsePaymentResult as parsePaymentResultLib } from "@/lib/payment-result";
import {
  Processing,
  PaymentMethodType,
  PageError,
  type PaymentMethodOption,
} from "@/modules/payment-methods/types";

const CARD_ID_NEW = "new";

const poll = async (
  store: Store,
  getCheckoutSession: () => Promise<CheckoutSession>,
  onSessionFetched: (session: CheckoutSession) => void,
  statusCheck: CheckoutStatus,
) => {
  logInfo("payment-confirmation", "GET request triggered to checkout/v3");
  const session = await getCheckoutSession();
  logInfo("payment-confirmation", "GET response received from checkout/v3", {
    sessionId: session.id,
    status: session.status,
  });

  const check3dsPassed =
    (await lib3ds.get3dsResult(store)) === lib3ds.Result.OK;

  const wasRedirectInitiated = await lib3ds.is3dsRedirectInitiated(store);
  const isCompleted = await lib3ds.is3dsCompleted(store);

  const redirectUrl = session.paymentResult.redirectUrl ?? "";

  const willRedirect =
    redirectUrl !== "" &&
    !check3dsPassed &&
    !wasRedirectInitiated &&
    !isCompleted;

  logInfo("payment-confirmation", "3DS redirect URL check completed", {
    sessionId: session.id,
    redirectUrl: redirectUrl || "(empty)",
    check3dsPassed,
    wasRedirectInitiated,
    isCompleted,
    willRedirect,
    status: session.status,
  });

  if (wasRedirectInitiated && !check3dsPassed) {
    logInfo(
      "payment-confirmation",
      "User returned from 3DS, marking as complete",
      {
        sessionId: session.id,
        status: session.status,
      },
    );

    await lib3ds.set3dsCompletedFlag(store);
    await lib3ds.clear3dsRedirectInitiated(store);

    const currentResult = await lib3ds.get3dsResult(store);
    if (currentResult === lib3ds.Result.NONE) {
      await store.set("3ds_result", "ok");
    }
  }

  const isOn3dsReturnPath = window.location.pathname.startsWith(toAppPath("/3ds/"));

  if (willRedirect && !isOn3dsReturnPath) {
    logInfo(
      "payment-confirmation",
      `3DS redirect needed - navigating to /3ds`,
      {
        sessionId: session.id,
        redirectUrl: redirectUrl || "(empty)",
        status: session.status,
      },
    );
    // Store the redirect URL in sessionStorage so /3ds page can access it
    // This avoids relying on SWR cache which might cause timing issues
    try {
      sessionStorage.setItem(`cosmos_3ds_redirect_${session.id}`, redirectUrl);
    } catch {
      // sessionStorage might be blocked, continue anyway
    }
    // Use window.location for navigation to ensure clean state.
    // withCheckoutId preserves the checkout ID in the URL across the hard navigation.
    window.location.href = toAppPath(withCheckoutId("/3ds"));
    return;
  }

  // Only update session cache when NOT redirecting to 3DS
  onSessionFetched(session);

  const shouldContinuePolling = shouldPoll(session);

  if (!shouldContinuePolling) {
    logDebug("payment-confirmation", "Polling stopped", {
      sessionId: session.id,
      status: session.status,
      statusCheck,
      shouldPoll: shouldPoll(session),
      statusChanged: statusCheck !== session.status,
    });
    return;
  }

  await wait(3000);

  return await poll(store, getCheckoutSession, onSessionFetched, statusCheck);
};

const shouldPoll = (session: CheckoutSession) =>
  CHECKOUT_STATUS_PROCESSING_PAYMENT.includes(
    session?.status ?? CheckoutStatus.UNKNOWN,
  );

export function PaymentConfirmationLegacy({
  onSessionFetched,
  onBack,
  cartValue,
}: PaymentConfirmationProps) {
  void onBack;
  void cartValue;
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const shouldShowFailureLoading = useMemo(() => {
    const pathname = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    const result = searchParams.get("result");
    const is3dsFailure = pathname.includes("3ds") && result === "failed";
    const isPaymentMethodFailure =
      pathname.includes("instalment/add-payment-method") &&
      pathname.includes("failed");
    const shouldShow = is3dsFailure || isPaymentMethodFailure;

    if (shouldShow) {
      logInfo(
        "payment-confirmation",
        "3DS or payment method failure detected in pathname",
        {
          pathname,
          result,
          is3dsFailure,
          isPaymentMethodFailure,
        },
      );
    }

    return shouldShow;
  }, [location.pathname, location.search]);

  const [isReporting3dsFailure, setIsReporting3dsFailure] = useState(
    shouldShowFailureLoading,
  );

  const { data: session, mutate: mutateSession } = useCheckoutSessionV3({
    skip: shouldShowFailureLoading,
  });
  const data = session; // session now contains all the data
  const ivrStatus = session?.ivrStatus;
  const isIvrFailed = useMemo(
    () => (ivrStatus ?? "").toUpperCase() === "FAILED",
    [ivrStatus],
  );
  const selectedPaymentMethod = useSelectedPaymentMethod();
  const { setSelectedPaymentMethod } = usePaymentPlansActions();
  const { data: savedCards } = useSavedCards({
    merchantId: data?.merchant?.id || "",
    orderId: data?.orderId,
    skip: shouldShowFailureLoading,
  });
  const canPayWithApple = (ctx: AppContext) =>
    ctx.session.country.applePayAllowed &&
    ctx.applePay.deviceSupports() &&
    (savedCards?.metaData?.applePayPreference ?? true);
  const { trigger: backFromPaymentMethodSelection } =
    useBackFromPaymentMethodSelection();
  const userPaymentMethods = useMemo(() => {
    const methods: PaymentMethodOption[] =
      savedCards?.cards.map((card) => ({
        paymentMethodType: PaymentMethodType.STORED_CARD,
        card,
      })) ?? [];

    return [...methods];
  }, [savedCards]);

  const [cvvModalOpen, setCvvModalOpen] = useState(false);
  const [processing, setProcessing] = useState<Processing>(Processing.NONE);
  const [isAgreed, setIsAgreed] = useState(false);
  const [isWalletSelected, setIsWalletSelected] = useState(false);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [isBottomSheetClosing, setIsBottomSheetClosing] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isCardModalOpen, setIsCardModalOpen] = useState(false);
  const [isApplePayModalOpen, setIsApplePayModalOpen] = useState(false);
  const hasManuallyClosedApplePayModalRef = useRef(false);
  const [cvv, setCvv] = useState("");
  const [cardForm, setCardForm] = useState({
    cardNumber: "",
    expiryDate: "",
    cvv: "",
    saveCard: false,
  });
  const [error, setError] = useState<PageError>(PageError.NONE);
  const [submittingGoBack, setSubmittingGoBack] = useState(false);
  const client = useCosmosClient();

  const hasReported3dsFailureRef = useRef(false);
  const ctxRef = useRef<AppContext | null>(null);
  const [screenCtx, setScreenCtx] = useState<AppContext | null>(null);
  const hasInitializedScreenCtxRef = useRef(false);

  // Track screen view and exit for drop-off analysis
  useScreenTracking({
    screenName: "payment_confirmation",
    viewedEvent: events.PAYMENT_CONFIRMATION_SCREEN_VIEWED,
    exitedEvent: events.PAYMENT_CONFIRMATION_SCREEN_EXITED,
    ctx: screenCtx,
  });

  // Update screen context for tracking when ctxRef changes
  // This effect runs after every render to check if ctxRef.current has been set
  // We use a ref to track initialization to prevent infinite loops
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!hasInitializedScreenCtxRef.current && ctxRef.current) {
      setScreenCtx(ctxRef.current);
      hasInitializedScreenCtxRef.current = true;
    }
  });

  useEffect(() => {
    const checkoutId = client.config().checkoutId;
    const storageKey = `reported_3ds_failure_${checkoutId}`;

    // Guard against duplicate calls within the same component instance
    if (hasReported3dsFailureRef.current) {
      logInfo(
        "payment-confirmation",
        "Blocked by ref guard - already reported in this component instance",
        {
          checkoutId,
        },
      );
      return;
    }

    if (shouldShowFailureLoading) {
      // Check sessionStorage to prevent duplicate calls across component remounts
      try {
        if (sessionStorage.getItem(storageKey) === "true") {
          // Already reported in this session, just update UI state
          logInfo(
            "payment-confirmation",
            "Blocked by sessionStorage - already reported in this browser session",
            {
              checkoutId,
              storageKey,
            },
          );
          logInfo(
            "payment-confirmation",
            "3DS failure detected, reporting to backend",
            {
              pathname: location.pathname,
              search: location.search,
            },
          );
          startTransition(() => {
            setIsReporting3dsFailure(false);
          });
          return;
        }
      } catch {
        logInfo(
          "payment-confirmation",
          "sessionStorage blocked, continuing with ref guard only",
          {
            checkoutId,
          },
        );
      }

      hasReported3dsFailureRef.current = true;

      // Mark as reported in sessionStorage before making the API call
      try {
        sessionStorage.setItem(storageKey, "true");
      } catch {
        // sessionStorage might be blocked, continue anyway
      }

      logInfo("payment-confirmation", "Calling report3dsFailure API", {
        checkoutId,
        storageKey,
      });

      client
        .report3dsFailure()
        .then((sessionData) => {
          logInfo("payment-confirmation", "report3dsFailure succeeded", {
            checkoutId,
            newStatus: sessionData.status,
          });
          mutateSession(sessionData, { revalidate: false });
          onSessionFetched(sessionData);
          startTransition(() => {
            setIsReporting3dsFailure(false);
          });
        })
        .catch(async (err) => {
          const typedErr = err as { response?: { status?: number } };
          const httpStatus = typedErr?.response?.status;

          logError(
            "payment-confirmation",
            "Failed to report 3DS failure",
            err instanceof Error ? err : new Error(String(err)),
            {
              error: err,
              checkoutId,
              httpStatus,
              is409: httpStatus === 409,
              sessionStorageValueAtError: (() => {
                try {
                  return sessionStorage.getItem(storageKey);
                } catch {
                  return "sessionStorage_blocked";
                }
              })(),
            },
          );
          await refetchCheckoutAfterFailed3dsReport(
            () => client.getCheckoutSessionV3(),
            mutateSession,
            onSessionFetched,
          );
          setTimeout(() => {
            startTransition(() => {
              setIsReporting3dsFailure(false);
            });
          }, 2000);
        });
    }
  }, [
    client,
    shouldShowFailureLoading,
    mutateSession,
    onSessionFetched,
    location.pathname,
    location.search,
  ]);

  const has3dsPassed = useMemo(() => {
    const pathname = location.pathname;
    const isPaymentMethodPath = pathname.includes(
      "instalment/add-payment-method/",
    );
    const hasSuccess = pathname.includes("success");
    const passed = isPaymentMethodPath && hasSuccess;

    if (isPaymentMethodPath) {
      logInfo("payment-confirmation", "3DS pathname status check", {
        pathname,
        isPaymentMethodPath,
        hasSuccess,
        passed,
      });
    }

    return passed;
  }, [location.pathname]);

  const handleCloseBottomSheet = () => {
    setIsBottomSheetClosing(true);
    setTimeout(() => {
      setIsBottomSheetClosing(false);
      setIsBottomSheetOpen(false);
    }, 300);
  };

  const handleCloseCardModal = () => {
    if (session) {
      emitCardFormTracking("new_card_form_closed", session);
      emitModalTracking("card_form_modal_closed", session, {
        modal_name: "card_form",
      });
    }
    setIsCardModalOpen(false);
    setCardForm({
      cardNumber: "",
      expiryDate: "",
      cvv: "",
      saveCard: false,
    });
  };

  const paymentMethodsRef = useRef<PaymentMethodOption[] | null>(null);
  const applePayClickHandlerRef = useRef<(() => Promise<void>) | null>(null);
  const walletAutoSelectedRef = useRef<boolean>(false);
  const lastActionRef = useRef<"wallet" | "card" | null>(null);
  const isSyncingRef = useRef<boolean>(false);
  const isPollingRef = useRef<boolean>(false);
  const pollingStatusRef = useRef<CheckoutStatus | null>(null);
  const hasEmittedPaymentMethodsShownRef = useRef<boolean>(false);
  const paymentLoadingStartTimeRef = useRef<number | null>(null);
  const slowPaymentLoadingIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (!session || !shouldPoll(session)) {
      // Reset polling state when we shouldn't poll anymore
      if (isPollingRef.current) {
        isPollingRef.current = false;
        pollingStatusRef.current = null;
      }
      return;
    }

    // Prevent starting a new poll if one is already in progress for the same status
    if (isPollingRef.current && pollingStatusRef.current === session.status) {
      return;
    }

    if (ctxRef.current?.getCheckoutSession && ctxRef.current?.store) {
      isPollingRef.current = true;
      pollingStatusRef.current = session.status;

      // Start polling - the poll function will handle its own recursion
      poll(
        ctxRef.current.store,
        ctxRef.current.getCheckoutSession,
        onSessionFetched,
        session.status,
      ).catch(() => {
        // Reset on error
        if (pollingStatusRef.current === session.status) {
          isPollingRef.current = false;
          pollingStatusRef.current = null;
        }
      });
    }
  }, [session?.status, onSessionFetched, session]);

  useEffect(() => {
    // Don't auto-select payment method if:
    // 1. We're currently syncing states
    // 2. Wallet covers whole downpayment (never auto-select in this case)
    if (
      isSyncingRef.current ||
      session?.paymentDetails?.canWalletCoverDownPayment
    ) {
      return;
    }

    autoSelectPayment(
      paymentMethodsRef,
      selectedPaymentMethod,
      setSelectedPaymentMethod,
    );
  }, [
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    session?.paymentDetails?.canWalletCoverDownPayment,
    isWalletSelected,
    savedCards, // Add savedCards as dependency since paymentMethods are derived from it
  ]);

  // Track payment methods shown
  useEffect(() => {
    if (
      !session ||
      hasEmittedPaymentMethodsShownRef.current ||
      session.status !== CheckoutStatus.AWAIT_PAYMENT_METHOD_SELECTION
    ) {
      return;
    }

    // Only track once when payment methods are available
    if (savedCards !== undefined) {
      // Device supports Apple Pay if the browser exposes the API (e.g. Safari); does not mean we showed it
      const deviceSupportsApplePay =
        "ApplePaySession" in window &&
        typeof (window as { ApplePaySession?: unknown }).ApplePaySession !==
          "undefined";
      const applePayAvailable =
        session.country.applePayAllowed &&
        deviceSupportsApplePay &&
        (savedCards?.metaData?.applePayPreference ?? true);

      emitPaymentMethodsShown(
        session,
        savedCards,
        applePayAvailable,
        deviceSupportsApplePay,
      );

      // Track Apple Pay availability
      if (applePayAvailable) {
        emitApplePayTracking("apple_pay_available", session);
        emitApplePayTracking("apple_pay_button_shown", session);
      }

      hasEmittedPaymentMethodsShownRef.current = true;
    }
  }, [session, savedCards]);

  // Auto-select wallet if available
  useEffect(() => {
    const hasWallet =
      session?.paymentDetails?.availableWalletBalance &&
      session.paymentDetails.availableWalletBalance.amount > 0;

    if (hasWallet && !walletAutoSelectedRef.current) {
      lastActionRef.current = "wallet";
      startTransition(() => {
        setIsWalletSelected(true);
      });
      walletAutoSelectedRef.current = true;
    } else if (!hasWallet) {
      walletAutoSelectedRef.current = false;
    }
  }, [session?.paymentDetails?.availableWalletBalance]);

  // Mutual exclusion: when wallet covers whole downpayment, only one can be selected
  useEffect(() => {
    if (
      !session?.paymentDetails?.canWalletCoverDownPayment ||
      isSyncingRef.current
    ) {
      return;
    }

    // Only act if both are selected - unselect based on lastAction
    if (isWalletSelected && selectedPaymentMethod) {
      // Prevent autoSelectPayment from interfering
      isSyncingRef.current = true;

      if (lastActionRef.current === "wallet") {
        // Wallet was selected last, so unselect card
        startTransition(() => {
          setSelectedPaymentMethod(undefined);
        });
      } else {
        // Card was selected last (or unknown), so unselect wallet
        startTransition(() => {
          setIsWalletSelected(false);
        });
      }

      // Reset sync flag after React processes the state change
      setTimeout(() => {
        isSyncingRef.current = false;
      }, 0);
    }
  }, [
    isWalletSelected,
    selectedPaymentMethod,
    session?.paymentDetails?.canWalletCoverDownPayment,
    setSelectedPaymentMethod,
  ]);

  const validateCardForm = () => {
    // Card number should be 19 characters (with spaces) or remove spaces and check for 16 digits
    const cardNumberDigits = cardForm.cardNumber.replace(/\s/g, "");
    const isCardLengthValid = [15, 16].includes(cardNumberDigits.length);
    const isCardNumberValid =
      isCardLengthValid && /^\d+$/.test(cardNumberDigits);

    // Expiry date should be in MM/YY format (5 characters)
    const isExpiryDateValid =
      cardForm.expiryDate.length === 5 &&
      /^\d{2}\/\d{2}$/.test(cardForm.expiryDate);

    // Check if expiry date is not in the past
    let hasFutureExpiry = false;
    if (isExpiryDateValid) {
      const [month, year] = cardForm.expiryDate.split("/");
      const expiryMonth = parseInt(month, 10);
      const expiryYear = 2000 + parseInt(year, 10); // Convert YY to YYYY

      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1; // getMonth() returns 0-11

      // Check if expiry is in the future
      if (expiryYear > currentYear) {
        hasFutureExpiry = true;
      } else if (expiryYear === currentYear && expiryMonth >= currentMonth) {
        hasFutureExpiry = true;
      }
    }

    // CVV should be 3-4 digits
    const isCvvValid =
      cardForm.cvv.length >= 3 &&
      cardForm.cvv.length <= 4 &&
      /^\d+$/.test(cardForm.cvv);

    return (
      isCardNumberValid && isExpiryDateValid && hasFutureExpiry && isCvvValid
    );
  };

  const handleAddCard = async () => {
    if (cardForm.cardNumber && cardForm.expiryDate && cardForm.cvv) {
      try {
        // Lazy cleanup: Clear 3DS state before new payment submission
        // This ensures each new attempt starts with a clean slate
        if (session?.id) {
          const store = newLocalStorageStore(window, session.id);
          await lib3ds.clear3dsState(store);
          lib3ds.clearSessionFlags(client.config().checkoutId);
          logInfo(
            "payment-confirmation",
            "Cleared 3DS state for new payment submission",
            {
              sessionId: session.id,
            },
          );
        }

        setProcessing(Processing.TOKENIZATION);
        setError(PageError.NONE);

        const newCard = {
          id: CARD_ID_NEW,
          name: `Card ending in ${cardForm.cardNumber.slice(-4)}`,
          type: "card" as const,
          last4: cardForm.cardNumber.slice(-4),
        };

        const { month, year } = parseCardDate(cardForm.expiryDate);

        // Track tokenization started
        if (session) {
          emitCardFormTracking("card_tokenization_started", session);
        }

        const { token, fingerprint, bin, last4, expiry_month, expiry_year } =
          await tokenizeCard(
            client,
            setError,
            session?.customerId ?? "",
            {
              number: cardForm.cardNumber.replace(/ /g, ""),
              expiryMonth: month,
              expiryYear: year,
              cvv: cardForm.cvv,
            },
            session,
          );

        // Track tokenization completed
        if (session) {
          emitCardFormTracking("card_tokenization_completed", session);
        }

        logInfo(
          "payment-confirmation",
          "POST request triggered to select-payment-method",
          {
            paymentMethod: PaymentMethod.CREDIT_CARD,
            hasToken: !!token,
          },
        );
        const newSession = await client.selectPaymentMethod(
          PaymentMethod.CREDIT_CARD,
          {
            token: token,
            termsAccepted: termsAccepted,
            documentsSigned: !documentSignatureIsPending,
            shouldUseWalletBalance: isWalletSelected,
            fingerprint: fingerprint,
            ...(bin !== undefined && { cardBin: bin }),
            ...(last4 !== undefined && { cardLast4: last4 }),
            cardExpiryMonth: String(expiry_month ?? month),
            cardExpiryYear: String(expiry_year ?? year),
          },
        );
        logInfo(
          "payment-confirmation",
          "POST response received from select-payment-method",
          {
            sessionId: newSession.id,
            status: newSession.status,
            hasToken: !!token,
            termsAccepted,
            documentsSigned: !documentSignatureIsPending,
            shouldUseWalletBalance: isWalletSelected,
          },
        );

        onSessionFetched(newSession);

        // Track payment submission completed for new card
        if (session) {
          emitPaymentSubmissionTracking(
            "payment_submission_completed",
            session,
            {
              payment_method_type: "card",
              card_id: CARD_ID_NEW,
              wallet_selected: isWalletSelected,
            },
          );
        }

        setSelectedPaymentMethod(newCard as PpsPaymentMethodDetails);

        handleCloseCardModal();

        setProcessing(Processing.NONE);
      } catch (err) {
        setProcessing(Processing.NONE);

        // Track payment submission failed
        if (session) {
          emitPaymentSubmissionTracking("payment_submission_failed", session, {
            payment_method_type: "card",
            card_id: CARD_ID_NEW,
            wallet_selected: isWalletSelected,
            error_message: err instanceof Error ? err.message : String(err),
          });
        }

        throw err;
      }
    }
  };

  const isSubmitting = processing === Processing.SUBMIT;

  const isProcessingPayment = CHECKOUT_STATUS_PROCESSING_PAYMENT.includes(
    session?.status ?? CheckoutStatus.UNKNOWN,
  );

  const currentLoadingState =
    isProcessingPayment ||
    has3dsPassed ||
    isReporting3dsFailure ||
    submittingGoBack;

  const shouldShowLoading = useMemo(
    () => currentLoadingState,
    [currentLoadingState],
  );

  // Handler for browser back button - goes back to payment plan selection
  const handleGoBack = useCallback(async () => {
    const ctx = ctxRef.current;

    if (ctx) {
      ctx.sendEvent(events.BROWSER_BACK_CLICKED);
    }

    if (session) {
      emitNavigationTracking("back_button_clicked", session, {
        from_page: "payment_confirmation",
      });
    }

    if (submittingGoBack) {
      return;
    }

    setSubmittingGoBack(true);

    try {
      const updatedSession = await backFromPaymentMethodSelection();
      setSubmittingGoBack(false);
      onSessionFetched(updatedSession);
    } catch (error) {
      setSubmittingGoBack(false);
      logError(
        "payment-confirmation",
        "Failed to go back from payment method selection (browser back)",
        error instanceof Error ? error : new Error(String(error)),
        { error },
      );
      // Fallback to navigation if API call fails
      if (ctx) {
        ctx.navigate(Page.PAYMENT_PLAN_SELECTION);
      }
    }
  }, [
    session,
    submittingGoBack,
    backFromPaymentMethodSelection,
    onSessionFetched,
  ]);

  // Intercept browser back button on confirmation page
  // This makes the browser back button behave the same as the UI back button
  useBrowserBackButton({
    onBack: handleGoBack,
    // Disable interception when:
    // - Page is in loading state (payment processing, 3DS redirect, etc.)
    // - Already submitting go back request
    // - Any other processing is happening
    enabled:
      !shouldShowLoading && processing === Processing.NONE && !submittingGoBack,
  });

  // Track slow payment loading
  useEffect(() => {
    if (!isProcessingPayment || !session) {
      // Clean up when payment processing stops
      if (slowPaymentLoadingIntervalRef.current !== null) {
        clearInterval(slowPaymentLoadingIntervalRef.current);
        slowPaymentLoadingIntervalRef.current = null;
      }
      paymentLoadingStartTimeRef.current = null;
      return;
    }

    // Start tracking when payment processing begins
    if (paymentLoadingStartTimeRef.current === null) {
      paymentLoadingStartTimeRef.current = Date.now();
    }

    const startTime = paymentLoadingStartTimeRef.current;
    const sessionRef = session; // Capture session for closure

    const checkSlowLoading = () => {
      // Check if still processing by checking session status
      const stillProcessing = CHECKOUT_STATUS_PROCESSING_PAYMENT.includes(
        sessionRef?.status ?? CheckoutStatus.UNKNOWN,
      );

      if (!stillProcessing || !sessionRef) {
        if (slowPaymentLoadingIntervalRef.current !== null) {
          clearInterval(slowPaymentLoadingIntervalRef.current);
          slowPaymentLoadingIntervalRef.current = null;
        }
        return;
      }

      const elapsedTimeMs = Date.now() - startTime;
      const elapsedTimeSeconds = Math.floor(elapsedTimeMs / 1000);

      // Emit first event after 60 seconds
      if (elapsedTimeSeconds >= 60) {
        emitSlowPaymentLoadingTracking(sessionRef, elapsedTimeMs);

        // Set up interval to emit every 30 seconds after the first 60 seconds
        if (slowPaymentLoadingIntervalRef.current === null) {
          slowPaymentLoadingIntervalRef.current = window.setInterval(() => {
            const currentStillProcessing =
              CHECKOUT_STATUS_PROCESSING_PAYMENT.includes(
                sessionRef?.status ?? CheckoutStatus.UNKNOWN,
              );

            if (!currentStillProcessing || !sessionRef) {
              if (slowPaymentLoadingIntervalRef.current !== null) {
                clearInterval(slowPaymentLoadingIntervalRef.current);
                slowPaymentLoadingIntervalRef.current = null;
              }
              return;
            }

            const currentElapsedTimeMs = Date.now() - startTime;
            emitSlowPaymentLoadingTracking(sessionRef, currentElapsedTimeMs);
          }, 30000); // Every 30 seconds
        }
      }
    };

    // Check immediately and then set up interval to check every second until 60 seconds
    checkSlowLoading();
    const checkInterval = setInterval(checkSlowLoading, 1000);

    // Cleanup function
    return () => {
      clearInterval(checkInterval);
      if (slowPaymentLoadingIntervalRef.current !== null) {
        clearInterval(slowPaymentLoadingIntervalRef.current);
        slowPaymentLoadingIntervalRef.current = null;
      }
    };
  }, [isProcessingPayment, session]);

  const loadingPage = useMemo(
    () => (
      <div className="fixed inset-0 bg-gray-100 flex flex-col z-10">
        <div className="w-full md:w-[480px] mx-auto bg-white min-h-screen md:rounded-t-[32px] md:min-h-[calc(100vh-24px)] md:mt-[24px] md:shadow-xs flex flex-col">
          <LoadingPage
            title={
              submittingGoBack
                ? t("loading.go_back_to_payment_plan")
                : t("loading.processing_payment")
            }
            message={t("loading.do_not_refresh")}
          />
        </div>
      </div>
    ),
    [t, submittingGoBack],
  );
  const selectedPaymentOption = (session?.paymentOptions ?? []).find(
    (paymentOption) =>
      paymentOption.offerId === (session?.selectedPaymentOptionId ?? ""),
  );
  const confirmHeadingText = useMemo(() => {
    return selectedPaymentOption?.tenure === 1
      ? t("payment.confirm_order")
      : t("payment.confirm_and_pay");
  }, [selectedPaymentOption?.tenure, t]);

  const documentSignatureIsPending =
    (session?.shouldShowDocumentSigning ?? false) && !isAgreed;

  // If shouldShowDocumentSigning is false, terms_accepted should always be true
  const termsAccepted =
    (session?.shouldShowDocumentSigning ?? false) ? isAgreed : true;

  const selectedCardIsNew = selectedPaymentMethod?.id === CARD_ID_NEW;
  const canSubmitPayment =
    selectedPaymentMethod !== undefined ||
    (isWalletSelected &&
      session?.paymentDetails?.canWalletCoverDownPayment === true);

  return (
    <>
      <AppWrapper
        visible={true}
        showLanguageSwitcher={shouldShowLoading}
        showLogo={shouldShowLoading}
        showCartButton={false}
        headerLeft={
          shouldShowLoading
            ? undefined
            : (ctx) => (
                <Button
                  onClick={async () => {
                    try {
                      ctx.sendEvent(events.HEADER_GO_BACK_CLICKED);
                      if (session) {
                        emitNavigationTracking("back_button_clicked", session, {
                          from_page: "payment_confirmation",
                        });
                      }

                      if (submittingGoBack) {
                        return;
                      }

                      setSubmittingGoBack(true);

                      const updatedSession =
                        await backFromPaymentMethodSelection();

                      setSubmittingGoBack(false);

                      onSessionFetched(updatedSession);
                    } catch (error) {
                      setSubmittingGoBack(false);
                      logError(
                        "payment-confirmation",
                        "Failed to go back from payment method selection",
                        error instanceof Error
                          ? error
                          : new Error(String(error)),
                        { error },
                      );
                      // Fallback to navigation if API call fails
                      ctx.navigate(Page.PAYMENT_PLAN_SELECTION);
                    }
                  }}
                  variant="neutral"
                  rounded
                  size="medium"
                  disabled={processing !== Processing.NONE || submittingGoBack}
                >
                  {ctx.localizedIcon(LOCALIZED_ICON_TYPE.ARROW_BACK)}
                </Button>
              )
        }
        withOverlay={() => isSubmitting && !shouldShowLoading}
        stickyFooter={
          shouldShowLoading || isIvrFailed
            ? undefined
            : (ctx: AppContext) => (
                <div className="relative">
                  <div className="space-y-4 flex flex-col items-center">
                    {ctx.session.shouldShowDocumentSigning === true && (
                      <>
                        <div className="flex items-center space-x-3 mb-6 cursor-pointer">
                          <label className="inline-flex items-center space-x-3 cursor-pointer select-none">
                            <input type="checkbox" className="hidden peer" />
                            <div>
                              <Checkbox
                                value={isAgreed}
                                onChange={() => {
                                  const newValue = !isAgreed;
                                  setIsAgreed(newValue);
                                  // Track terms agreement
                                  if (session) {
                                    emitDocumentTracking(
                                      newValue
                                        ? "terms_agreed"
                                        : "terms_disagreed",
                                      session,
                                    );
                                  }
                                }}
                              />
                            </div>
                          </label>
                          <span
                            className="text-sm text-[#16181D] font-medium leading-tight text-left flex items-center hover:text-gray-800 transition-colors gap-2"
                            onClick={() => {
                              ctx.sendEvent(
                                events.VIEW_FINANCE_DOCUMENTS_CLICKED,
                              );
                              if (session) {
                                emitDocumentTracking(
                                  "finance_documents_viewed",
                                  session,
                                );
                                emitModalTracking(
                                  "documents_modal_opened",
                                  session,
                                  { modal_name: "documents" },
                                );
                              }
                              setIsBottomSheetOpen(true);
                              setShowError(false);
                            }}
                          >
                            {t("payment_confirmation.agree_terms")}
                            <span>
                              {ctx.localizedIcon(
                                LOCALIZED_ICON_TYPE.CHEVRON_FORWARD,
                              )}
                            </span>
                          </span>
                        </div>
                        {showError && (
                          <p className="text-red-500 text-xs text-center">
                            {t("payment_confirmation.please_agree")}
                          </p>
                        )}
                      </>
                    )}

                    {/* Wallet contribution explanation */}
                    {isWalletSelected &&
                      ctx.session.paymentDetails?.walletDownPaymentAmount &&
                      ctx.session.paymentDetails.walletDownPaymentAmount
                        .amount > 0 && (
                        <div className="text-center mb-3">
                          <div className="text-sm text-[#585B7A]">
                            <span className="inline-flex items-center">
                              <MoneyAmount
                                amount={
                                  ctx.session.paymentDetails
                                    .walletDownPaymentAmount.amount
                                }
                                currency={
                                  ctx.session.paymentDetails
                                    .walletDownPaymentAmount.currency
                                }
                                language={i18n.language}
                                className="inline-flex"
                              />
                            </span>{" "}
                            {t("payment_confirmation.wallet_contribution")}
                          </div>
                        </div>
                      )}

                    <Button
                      onClick={async () => {
                        try {
                          logInfo("payment-confirmation", "Clicked to pay", {
                            documentSignatureIsPending,
                            isWalletSelected,
                            hasPaymentDetails: !!ctx.session.paymentDetails,
                          });

                          ctx.sendEvent(events.CONTINUE_CLICKED, {
                            continue_from: "confirmation",
                            document_signature_is_pending:
                              documentSignatureIsPending,
                            is_wallet_selected: isWalletSelected,
                            payment_details: JSON.stringify(
                              ctx.session.paymentDetails,
                            ),
                          });

                          if (documentSignatureIsPending) {
                            setShowError(true);
                            return;
                          }
                          if (processing !== Processing.NONE) return;

                          // Track payment submission started
                          const paymentMethodType =
                            (selectedPaymentMethod?.type ?? "") === "apple_pay"
                              ? "apple_pay"
                              : isWalletSelected &&
                                  ctx.session.paymentDetails
                                    ?.canWalletCoverDownPayment &&
                                  !selectedPaymentMethod
                                ? "wallet_only"
                                : "card";
                          const amountToPay =
                            isWalletSelected &&
                            ctx.session.paymentDetails
                              ?.cardDownPaymentAmountWithWallet
                              ? ctx.session.paymentDetails
                                  .cardDownPaymentAmountWithWallet.amount
                              : ctx.selectedPayment().amountToPayNow;

                          if (session) {
                            emitPaymentSubmissionTracking(
                              "payment_submission_started",
                              session,
                              {
                                payment_method_type: paymentMethodType,
                                card_id:
                                  selectedPaymentMethod?.id !== CARD_ID_NEW
                                    ? selectedPaymentMethod?.id
                                    : undefined,
                                wallet_selected: isWalletSelected,
                                amount: amountToPay,
                                document_signature_pending:
                                  documentSignatureIsPending,
                              },
                            );
                          }

                          // Handle wallet-only payment when wallet covers the whole downpayment
                          if (
                            isWalletSelected &&
                            ctx.session.paymentDetails
                              ?.canWalletCoverDownPayment &&
                            !selectedPaymentMethod
                          ) {
                            return await payWithWalletOnly(
                              setProcessing,
                              onSessionFetched,
                              client,
                              {
                                termsAccepted: termsAccepted,
                                documentsSigned: !documentSignatureIsPending,
                              },
                              session,
                            );
                          }

                          if (
                            (selectedPaymentMethod?.type ?? "") === "apple_pay"
                          ) {
                            return await payWithApple(
                              window,
                              setProcessing,
                              isWalletSelected,
                              client,
                              ctx,
                              onSessionFetched,
                              {
                                termsAccepted: termsAccepted,
                                documentsSigned: !documentSignatureIsPending,
                                shouldUseWalletBalance: isWalletSelected,
                              },
                            );
                          }

                          const paymentMethod = (
                            paymentMethodsRef.current ?? []
                          ).find(
                            (paymentMethod) =>
                              (paymentMethod.card?.id ?? 0) + "" ===
                              (selectedPaymentMethod?.id ?? ""),
                          );
                          if (!paymentMethod) {
                            logError(
                              "payment-confirmation",
                              "Could not find payment method object",
                              undefined,
                              {
                                selectedPaymentMethodId:
                                  selectedPaymentMethod?.id,
                              },
                            );
                            return;
                          }

                          if (paymentMethod.card?.isDisabledCvv ?? false) {
                            await payWithExistingCard(
                              setProcessing,
                              onSessionFetched,
                              client,
                              selectedPaymentMethod?.id ?? "",
                              undefined,
                              {
                                termsAccepted: termsAccepted,
                                documentsSigned: !documentSignatureIsPending,
                                shouldUseWalletBalance: isWalletSelected,
                              },
                              paymentMethod.card?.fingerprint,
                              session,
                              paymentMethod.card,
                            );

                            return;
                          }

                          ctx.sendEvent(events.CVV_MODAL_SHOWN);
                          if (session) {
                            emitModalTracking("cvv_modal_opened", session, {
                              modal_name: "cvv",
                            });
                          }
                          setCvvModalOpen(true);
                        } catch (error) {
                          logError(
                            "payment-confirmation",
                            "Error processing payment",
                            error instanceof Error
                              ? error
                              : new Error(String(error)),
                            { error },
                          );
                        }
                      }}
                      disabled={
                        selectedCardIsNew ||
                        !canSubmitPayment ||
                        isSubmitting ||
                        documentSignatureIsPending
                      }
                      className="w-full h-[54px] py-3 rounded-full font-semibold hover:bg-purple-700 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
                      loading={isSubmitting}
                    >
                      {isProcessingPayment ? (
                        <div className="flex items-center justify-center space-x-2 text-[#7E82AD]">
                          <span>
                            {t("payment_confirmation.processing_payment")}
                          </span>
                        </div>
                      ) : (
                        <div className="flex justify-center gap-2">
                          {selectedPaymentOption?.tenure === 1 ? (
                            <span>
                              {t(
                                "payment_confirmation.complete_order_pay_later",
                              )}
                            </span>
                          ) : isWalletSelected &&
                            ctx.session.paymentDetails
                              ?.canWalletCoverDownPayment &&
                            !selectedPaymentMethod ? (
                            <span>
                              {t("payment_confirmation.pay_with_wallet")}
                            </span>
                          ) : isWalletSelected &&
                            ctx.session.paymentDetails
                              ?.canWalletCoverDownPayment ? (
                            <span>
                              {t(
                                "payment_confirmation.pay_downpayment_with_wallet",
                              )}
                            </span>
                          ) : (
                            <>
                              {t("payment_confirmation.pay")}{" "}
                              <MoneyAmount
                                amount={
                                  isWalletSelected &&
                                  ctx.session.paymentDetails
                                    ?.cardDownPaymentAmountWithWallet
                                    ? ctx.session.paymentDetails
                                        .cardDownPaymentAmountWithWallet.amount
                                    : ctx.selectedPayment().amountToPayNow
                                }
                                currency={ctx.session.order.currency}
                                language={i18n.language}
                              />
                            </>
                          )}
                        </div>
                      )}
                    </Button>
                  </div>
                </div>
              )
        }
        stickyFooterWithShadow={true}
      >
        {(ctx) => {
          // Store context in ref for use in effects
          ctxRef.current = ctx;

          const walletBalance = ctx.session.paymentDetails
            ?.availableWalletBalance ?? {
            amount: 0,
            currency: "",
          };
          const hasWalletBalance = walletBalance.amount > 0;

          // Set up Apple Pay click handler
          applePayClickHandlerRef.current = async () => {
            if (processing !== Processing.NONE) return;
            await payWithApple(
              window,
              setProcessing,
              isWalletSelected,
              client,
              ctx,
              onSessionFetched,
              {
                shouldUseWalletBalance: isWalletSelected,
                documentsSigned:
                  selectedPaymentOption?.needsDocumentSigning ?? undefined,
                termsAccepted,
              },
            );
          };

          const paymentMethods = canPayWithApple(ctx)
            ? [
                {
                  paymentMethodType: PaymentMethodType.APPLE,
                  card: undefined,
                },
                ...userPaymentMethods,
              ]
            : userPaymentMethods;

          paymentMethodsRef.current = paymentMethods;

          // Show loading page if processing payment OR if 3DS has passed
          // Using memoized component to prevent re-render flicker
          if (shouldShowLoading) {
            return loadingPage;
          }

          // Show error screen if IVR status indicates failure or session is invalidated
          if (
            (ivrStatus ?? "").toUpperCase() === "FAILED" ||
            session?.status === CheckoutStatus.INVALIDATED
          ) {
            return <ErrorScreen ctx={ctx} />;
          }

          const [hasPaymentError, paymentError] = parsePaymentResultLib(
            ctx.session.paymentResult,
            getPaymentErrorMessage,
          );

          // Calculate if Apple Pay modal should be shown
          const applePayIsSelected =
            (selectedPaymentMethod?.type ?? "") === "apple_pay";
          const hasSessionError =
            (session?.error?.type ?? SessionError.NONE) !== SessionError.NONE;
          const shouldShowApplePayModal =
            !hasSessionError &&
            !hasPaymentError &&
            applePayIsSelected &&
            ivrStatus === "ACCEPTED";

          if (
            shouldShowApplePayModal &&
            !isApplePayModalOpen &&
            !hasManuallyClosedApplePayModalRef.current
          ) {
            setIsApplePayModalOpen(true);
          }

          return (
            <>
              {hasPaymentError && (
                <div className="px-6 mb-2">
                  <Banner type={BannerType.ERROR}>{paymentError}</Banner>
                  <p className="mt-2 text-sm text-gray-600">
                    {t("payment_confirmation.error_recovery_hint")}
                  </p>
                </div>
              )}

              <div className="flex items-center mb-6 px-6">
                <h2 className="text-[24px] font-semibold text-gray-900">
                  {confirmHeadingText}
                </h2>
              </div>

              <div className="space-y-6 px-6 pb-6">
                <div>
                  {((savedCards?.cards && savedCards.cards.length > 0) ||
                    canPayWithApple(ctx)) && (
                    <div className="mb-4 text-[#585B7A]">
                      {t("payment_confirmation.payment_method")}
                    </div>
                  )}
                  <CardMenu>
                    {paymentMethods.map((method) => {
                      const isCardSelected =
                        method.paymentMethodType === PaymentMethodType.APPLE
                          ? selectedPaymentMethod?.type === "apple_pay"
                          : selectedPaymentMethod?.id === method.card?.id + "";

                      return (
                        <CardMenuItem
                          key={method.card?.id}
                          icon={(() => {
                            if (
                              method.paymentMethodType ===
                              PaymentMethodType.APPLE
                            )
                              return <CardIcon type={CardFlag.APPLE_PAY} />;

                            if (
                              method.card?.paymentMethod ===
                              PaymentMethod.MADA_CARD
                            )
                              return <CardIcon type={CardFlag.MADA} />;

                            return cardIconByScheme(
                              method.card
                                ? resolveScheme(method.card)
                                : CardScheme.UNKNOWN,
                            );
                          })()}
                          onClick={() => {
                            const card = method.card;
                            ctx.sendEvent(
                              events.SELECTED_CARD,
                              card
                                ? {
                                    card_id: method.card?.id,
                                    card_scheme: encodeCardScheme(
                                      method.card?.scheme ?? CardScheme.UNKNOWN,
                                    ),
                                    card_scheme_local:
                                      encodeCardScheme(
                                        method.card?.schemeLocal ??
                                          CardScheme.UNKNOWN,
                                      ) ?? "",
                                    card_is_debit: method.card?.isDebit,
                                    card_is_prepaid: method.card?.isPrepaid,
                                    card_is_auto_charge:
                                      method.card?.isAutoCharge,
                                    card_is_bin_blacklisted:
                                      method.card?.isBinBlacklisted,
                                    card_is_disabled_cvv:
                                      method.card?.isDisabledCvv,
                                  }
                                : {},
                            );

                            // Track payment method selection
                            if (session) {
                              if (
                                method.paymentMethodType ===
                                PaymentMethodType.APPLE
                              ) {
                                emitPaymentMethodSelectionTracking(
                                  "apple_pay_selected",
                                  session,
                                  {
                                    payment_method_type: "apple_pay",
                                  },
                                );
                              } else if (method.card) {
                                emitPaymentMethodSelectionTracking(
                                  "payment_method_selected",
                                  session,
                                  {
                                    payment_method_type: "card",
                                    card_id: method.card.id,
                                    card_scheme:
                                      encodeCardScheme(
                                        method.card.scheme ??
                                          CardScheme.UNKNOWN,
                                      ) ?? undefined,
                                    card_last4: method.card.last4,
                                    card_is_debit: method.card.isDebit,
                                    card_is_prepaid: method.card.isPrepaid,
                                    card_is_auto_charge:
                                      method.card.isAutoCharge,
                                  },
                                );
                              }
                            }

                            lastActionRef.current = "card";
                            selectPaymentMethod(
                              setSelectedPaymentMethod,
                              method,
                            );
                          }}
                          selected={isCardSelected}
                          showChevron={false}
                          rtl={ctx.rtl}
                          rightSlot={
                            <div
                              className={`w-5 h-5 rounded-full border-2 ${
                                isCardSelected
                                  ? "border-purple-600 bg-purple-600"
                                  : "border-gray-300"
                              }`}
                            >
                              {isCardSelected && (
                                <div className="w-full h-full rounded-full bg-white scale-50"></div>
                              )}
                            </div>
                          }
                        >
                          <h2>{cardItemName(method)}</h2>
                          <p>
                            {method.paymentMethodType ===
                            PaymentMethodType.APPLE
                              ? (method.card?.last4 ?? "")
                              : `**** ${method.card?.last4 ?? ""}`}
                          </p>
                        </CardMenuItem>
                      );
                    })}
                    <CardMenuItem
                      key={"new-card-option"}
                      icon={<NewCardIcon />}
                      onClick={() => {
                        ctx.sendEvent(events.ADD_NEW_CARD_CLICKED);

                        if (!documentSignatureIsPending) {
                          if (session) {
                            emitCardFormTracking(
                              "new_card_form_opened",
                              session,
                            );
                            emitModalTracking(
                              "card_form_modal_opened",
                              session,
                              { modal_name: "card_form" },
                            );
                          }
                          setIsCardModalOpen(true);
                        }
                      }}
                      rtl={ctx.rtl}
                      className={
                        documentSignatureIsPending
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }
                    >
                      <h2>{t("payment_confirmation.add_new_card")}</h2>
                    </CardMenuItem>
                  </CardMenu>

                  {hasWalletBalance && walletBalance.amount > 0 && (
                    <div className="mt-6 text-[#585B7A]">
                      {t("payment_confirmation.available_savings")}
                    </div>
                  )}
                  {/* Wallet Payment Option */}
                  {hasWalletBalance && walletBalance.amount > 0 && (
                    <div className="mt-4">
                      <WalletMenuItem
                        walletBalance={walletBalance}
                        isSelected={isWalletSelected}
                        onToggle={(value) => {
                          ctx.sendEvent(
                            value
                              ? events.WALLET_SELECTED
                              : events.WALLET_DESELECTED,
                            {
                              wallet_balance: walletBalance.amount,
                            },
                          );

                          // Track wallet selection/deselection
                          if (session) {
                            emitPaymentMethodSelectionTracking(
                              value
                                ? "payment_method_selected"
                                : "payment_method_deselected",
                              session,
                              {
                                payment_method_type: "wallet",
                                wallet_balance: walletBalance.amount,
                              },
                            );
                          }

                          if (value) {
                            lastActionRef.current = "wallet";
                          }
                          setIsWalletSelected(value);
                        }}
                      />
                    </div>
                  )}
                </div>
                <div className="bg-[#E5E5E5] h-px"></div>
                <div className="flex items-center justify-between mb-3 ">
                  <div className="text-[14px] font-medium leading-6 tracking-[-0.21px] text-[#585B7A]">
                    {t("payment.order_amount")}
                  </div>
                  <div className="text-[14px] font-medium leading-6 tracking-[-0.21px] text-[#585B7A]">
                    <MoneyAmount
                      amount={
                        ctx.selectedPayment().paymentOption
                          ?.totalToBeRepaidAmount?.amount ??
                        ctx.session.orderTotalAmount.amount
                      }
                      currency={ctx.session.order.currency}
                      language={i18n.language}
                    />
                  </div>
                </div>

                {isWalletSelected &&
                  ctx.session.paymentDetails?.walletDownPaymentAmount &&
                  ctx.session.paymentDetails.walletDownPaymentAmount.amount >
                    0 && (
                    <div className="flex items-center justify-between -mt-2 mb-3">
                      <div className="text-[14px] font-medium leading-6 tracking-[-0.21px] text-[#585B7A]">
                        {t("payment_confirmation.wallet_applied")}
                      </div>
                      <div className="text-[14px] font-medium leading-6 tracking-[-0.21px] text-[#128351]">
                        <span className="mr-1">-</span>
                        <MoneyAmount
                          amount={
                            ctx.session.paymentDetails.walletDownPaymentAmount
                              .amount
                          }
                          currency={
                            ctx.session.paymentDetails.walletDownPaymentAmount
                              .currency
                          }
                          language={i18n.language}
                        />
                      </div>
                    </div>
                  )}

                {selectedPaymentOption &&
                  selectedPaymentOption.tenure !== 1 && (
                    <div
                      className={`flex items-center justify-between ${
                        isWalletSelected &&
                        ctx.session.paymentDetails?.walletDownPaymentAmount &&
                        ctx.session.paymentDetails.walletDownPaymentAmount
                          .amount > 0
                          ? "-mt-2"
                          : ""
                      }`}
                    >
                      <div className="text-base font-semibold leading-[26px] tracking-[-0.24px] text-[#212442]">
                        {t("order.due_today")}
                      </div>
                      <div className="text-base font-semibold leading-[26px] tracking-[-0.24px] text-[#212442]">
                        <MoneyAmount
                          amount={
                            isWalletSelected &&
                            ctx.session.paymentDetails
                              ?.cardDownPaymentAmountWithWallet
                              ? ctx.session.paymentDetails
                                  .cardDownPaymentAmountWithWallet.amount
                              : selectedPaymentOption?.downPaymentAmount
                                  ?.amount || 0
                          }
                          currency={ctx.session.order.currency}
                          language={i18n.language}
                        />
                      </div>
                    </div>
                  )}
                {selectedPaymentOption && selectedPaymentOption.tenure > 1 && (
                  <div className="text-xs font-medium leading-5 tracking-[-0.18px] text-[#7E82AD] -mt-[22px]">
                    {t(
                      i18n.language.startsWith("ar") &&
                        selectedPaymentOption.tenure >= 11
                        ? "payment_confirmation.payment_of_total_one"
                        : "payment_confirmation.payment_of_total",
                      {
                        current: 1,
                        total: selectedPaymentOption.tenure,
                      },
                    )}
                  </div>
                )}
              </div>
              <DocumentsModal
                ctx={ctx}
                isOpen={isBottomSheetOpen}
                isClosing={isBottomSheetClosing}
                onClose={() => {
                  ctx.sendEvent(events.CLOSE_MODAL_CLICKED, {
                    modal_name: "finance_documents",
                  });
                  if (session) {
                    emitModalTracking("documents_modal_closed", session, {
                      modal_name: "documents",
                    });
                  }
                  handleCloseBottomSheet();
                }}
                onAgree={(agreed) => {
                  setIsAgreed(agreed);
                  // Track terms agreement from modal
                  if (session) {
                    emitDocumentTracking(
                      agreed ? "terms_agreed" : "terms_disagreed",
                      session,
                    );
                  }
                  if (agreed) {
                    setShowError(false);
                    handleCloseBottomSheet();
                  }
                }}
                isAgreed={isAgreed}
              />
              {cvvModalOpen && !isProcessingPayment && (
                <Modal
                  onClose={() => {
                    if (session) {
                      emitModalTracking("cvv_modal_closed", session, {
                        modal_name: "cvv",
                      });
                    }
                    setCvvModalOpen(false);
                    setCvv("");
                  }}
                  showCloseButton
                  stickyFooter={
                    <Button
                      size="large"
                      onClick={async () => {
                        try {
                          ctx.sendEvent(events.CONTINUE_CLICKED, {
                            continue_from: "cvv_modal",
                          });

                          // Track CVV submission started
                          if (session) {
                            emitPaymentSubmissionTracking(
                              "cvv_submission_started",
                              session,
                              {
                                payment_method_type: "card",
                                card_id: selectedPaymentMethod?.id ?? undefined,
                                wallet_selected: isWalletSelected,
                              },
                            );
                          }

                          const selectedCard = savedCards?.cards?.find(
                            (card) =>
                              String(card.id) === selectedPaymentMethod?.id,
                          );

                          await payWithExistingCard(
                            setProcessing,
                            onSessionFetched,
                            client,
                            selectedPaymentMethod?.id ?? "",
                            cvv,
                            {
                              termsAccepted: termsAccepted,
                              documentsSigned: !documentSignatureIsPending,
                              shouldUseWalletBalance: isWalletSelected,
                            },
                            selectedCard?.fingerprint,
                            session,
                            selectedCard,
                          );
                          if (session) {
                            emitPaymentSubmissionTracking(
                              "cvv_submission_completed",
                              session,
                              {
                                payment_method_type: "card",
                                card_id: selectedPaymentMethod?.id ?? undefined,
                                wallet_selected: isWalletSelected,
                              },
                            );
                          }
                          setCvvModalOpen(false);
                          setCvv("");
                        } catch (error) {
                          if (session) {
                            emitPaymentSubmissionTracking(
                              "cvv_submission_failed",
                              session,
                              {
                                payment_method_type: "card",
                                card_id: selectedPaymentMethod?.id ?? undefined,
                                wallet_selected: isWalletSelected,
                                error_message:
                                  error instanceof Error
                                    ? error.message
                                    : String(error),
                              },
                            );
                          }
                          logError(
                            "payment-confirmation",
                            "Error processing CVV payment",
                            error instanceof Error
                              ? error
                              : new Error(String(error)),
                            { error },
                          );
                          setCvvModalOpen(false);
                          setCvv("");
                        }
                      }}
                      disabled={isSubmitting || !cvv.trim()}
                      loading={isSubmitting}
                    >
                      {t("payment_confirmation.cvv_modal_pay_button")}
                    </Button>
                  }
                >
                  <div className="px-6 pt-6 pb-3">
                    <div className="flex items-center justify-between">
                      <div className="w-[40px]"></div>
                      <h3 className="text-xl font-semibold text-gray-900 pt-6">
                        {t("payment_confirmation.cvv_modal_title")}
                      </h3>
                      <div className="w-[40px]"></div>
                    </div>
                  </div>

                  <div className="p-6 overflow-y-auto flex-1">
                    <div>
                      <InputText
                        type="text"
                        placeholder={t(
                          "payment_confirmation.cvv_modal_placeholder",
                        )}
                        value={cvv}
                        onChange={(e) => setCvv(e.target.value)}
                        strict={/^[0-9]*$/}
                        maxLength={4}
                      />
                    </div>
                  </div>
                </Modal>
              )}
              {/* Apple Pay Modal */}
              {shouldShowApplePayModal && (
                <ApplePayModal
                  isOpen={isApplePayModalOpen}
                  onClose={() => {
                    setIsApplePayModalOpen(false);
                    hasManuallyClosedApplePayModalRef.current = true;
                  }}
                  onPayClick={() => {
                    applePayClickHandlerRef.current?.()?.catch((error) => {
                      logError(
                        "payment-confirmation",
                        "Error processing Apple Pay",
                        error instanceof Error
                          ? error
                          : new Error(String(error)),
                        { error },
                      );
                    });
                  }}
                  downPaymentAmount={amountToPay(ctx, isWalletSelected)}
                  currency={
                    session?.order?.currency
                      ? Currency[session.order.currency]
                      : undefined
                  }
                />
              )}
              {isCardModalOpen ? (
                <Modal
                  onClose={handleCloseCardModal}
                  showCloseButton
                  stickyFooter={
                    <Button
                      size="large"
                      variant="primary"
                      onClick={() => {
                        ctx.sendEvent(events.ADD_NEW_CARD_SUBMITTED);
                        if (session) {
                          emitCardFormTracking(
                            "new_card_form_submitted",
                            session,
                          );
                        }
                        handleAddCard().catch((error) => {
                          if (session) {
                            emitCardFormTracking(
                              "card_form_validation_error",
                              session,
                              {
                                validation_errors: [
                                  error instanceof Error
                                    ? error.message
                                    : String(error),
                                ],
                              },
                            );
                          }
                          logError(
                            "payment-confirmation",
                            "Error adding new card",
                            error instanceof Error
                              ? error
                              : new Error(String(error)),
                            { error },
                          );
                        });
                      }}
                      disabled={
                        !validateCardForm() || documentSignatureIsPending
                      }
                      className="disabled:bg-gray-300 disabled:cursor-not-allowed"
                      loading={processing === Processing.TOKENIZATION}
                    >
                      {t("payment.add_card")}
                    </Button>
                  }
                >
                  <CardForm
                    session={session ?? emptySession}
                    topContent={
                      <>
                        {error === PageError.TOKENIZATION_FAILED && (
                          <Banner type={BannerType.ERROR}>
                            {t("payment_confirmation.tokenization_error")}
                          </Banner>
                        )}
                      </>
                    }
                    cardForm={cardForm}
                    setCardForm={setCardForm}
                    t={t}
                  />
                </Modal>
              ) : null}
            </>
          );
        }}
      </AppWrapper>
    </>
  );
}

export function PaymentConfirmation(props: PaymentConfirmationProps) {
  return <PaymentMethodsView onSessionFetched={props.onSessionFetched} />;
}

const payWithExistingCard = async (
  setProcessing: (processing: Processing) => void,
  onSessionFetched: (session: CheckoutSession) => void,
  client: CosmosClient,
  cardId: string,
  cvv?: string,
  options?: {
    termsAccepted?: boolean;
    documentsSigned?: boolean;
    shouldUseWalletBalance?: boolean;
  },
  fingerprint?: string,
  session?: CheckoutSession,
  card?: StoredCard,
) => {
  if (session?.id) {
    const store = newLocalStorageStore(window, session.id);
    await lib3ds.clear3dsState(store);
    lib3ds.clearSessionFlags(client.config().checkoutId);
    logInfo(
      "payment-confirmation",
      "Cleared 3DS state for existing card payment",
      {
        sessionId: session.id,
      },
    );
  }

  setProcessing(Processing.SUBMIT);

  try {
    logInfo(
      "payment-confirmation",
      "POST request triggered to select-payment-method",
      {
        paymentMethod: PaymentMethod.CREDIT_CARD,
        hasCardId: !!cardId,
      },
    );
    const newSession = await client.selectPaymentMethod(
      PaymentMethod.CREDIT_CARD,
      {
        cardId,
        cvv,
        termsAccepted: options?.termsAccepted,
        documentsSigned: options?.documentsSigned,
        shouldUseWalletBalance: options?.shouldUseWalletBalance,
        fingerprint,
        ...(card !== undefined && {
          cardBin: card.bin,
          cardLast4: card.last4,
          cardExpiryMonth: String(card.expiryMonth),
          cardExpiryYear: String(card.expiryYear),
        }),
      },
    );
    logInfo(
      "payment-confirmation",
      "POST response received from select-payment-method",
      {
        sessionId: newSession.id,
        status: newSession.status,
        paymentMethod: PaymentMethod.CREDIT_CARD,
        hasCardId: !!cardId,
        hasCvv: !!cvv,
        termsAccepted: options?.termsAccepted,
        documentsSigned: options?.documentsSigned,
        shouldUseWalletBalance: options?.shouldUseWalletBalance,
      },
    );

    // Track payment submission completed
    if (session) {
      emitPaymentSubmissionTracking("payment_submission_completed", session, {
        payment_method_type: "card",
        card_id: cardId,
        wallet_selected: options?.shouldUseWalletBalance ?? false,
      });
    }

    onSessionFetched(newSession);

    setProcessing(Processing.NONE);
  } catch (err) {
    // Track payment submission failed
    if (session) {
      emitPaymentSubmissionTracking("payment_submission_failed", session, {
        payment_method_type: "card",
        card_id: cardId,
        wallet_selected: options?.shouldUseWalletBalance ?? false,
        error_message: err instanceof Error ? err.message : String(err),
      });
    }

    // Check if error has session attached (backward compatible)
    const errorSession = (err as { session?: CheckoutSession }).session;
    if (errorSession) {
      setProcessing(Processing.NONE);
      onSessionFetched(errorSession);
      throw err;
    }

    // Attempt smart recovery for HTTP errors (e.g., 409 Conflict)
    try {
      const recoveryResult = await handleHttpError(err as ApiError, {
        refetchSession: async () => {
          const freshData = await client.getCheckoutSessionV3();
          return CksCheckoutStateSchema.parse(freshData);
        },
        currentSession: session,
        endpoint: "select-payment-method",
        httpMethod: "POST",
        requestContext: {
          paymentMethod: "CREDIT_CARD",
          cardId,
          hasWallet: options?.shouldUseWalletBalance,
        },
      });

      setProcessing(Processing.NONE);

      if (recoveryResult.recovered) {
        // Successfully recovered - navigate based on actual session state
        logInfo(
          "payment-confirmation",
          "Recovered from payment method error via session refetch",
          {
            recoveredStatus: recoveryResult.session.status,
            originalError: (err as ApiError).response?.status,
          },
        );
        onSessionFetched(recoveryResult.session);
        return; // Don't throw - we recovered successfully
      }

      // Recovery failed - pass error session to show error screen
      onSessionFetched(recoveryResult.session);
      throw err;
    } catch {
      setProcessing(Processing.NONE);
      // If recovery itself failed, throw original error
      throw err;
    }
  }
};

const payWithApple = async (
  window: Window,
  setProcessing: (processing: Processing) => void,
  isWalletSelected: boolean,
  client: CosmosClient,
  ctx: AppContext,
  onSessionFetched: (session: CheckoutSession) => void,
  options?: {
    termsAccepted?: boolean;
    documentsSigned?: boolean;
    shouldUseWalletBalance?: boolean;
  },
) => {
  if (ctx.session?.id) {
    const store = newLocalStorageStore(window, ctx.session.id);
    await lib3ds.clear3dsState(store);
    lib3ds.clearSessionFlags(client.config().checkoutId);
    logInfo("payment-confirmation", "Cleared 3DS state for Apple Pay payment", {
      sessionId: ctx.session.id,
    });
  }

  setProcessing(Processing.SUBMIT);

  // Track Apple Pay initiated
  emitApplePayTracking("apple_pay_initiated", ctx.session);

  try {
    // If shouldShowIvr is true, submit with null token
    if (ctx.session.shouldShowIvr) {
      logInfo(
        "payment-confirmation",
        "Apple Pay: shouldShowIvr is true, submitting with null token",
      );

      logInfo(
        "payment-confirmation",
        "POST request triggered to select-payment-method",
        {
          paymentMethod: PaymentMethod.APPLE_PAY,
          isIvr: true,
        },
      );
      const newSession = await client.selectPaymentMethod(
        PaymentMethod.APPLE_PAY,
        {
          token: null,
          termsAccepted: options?.termsAccepted,
          documentsSigned: options?.documentsSigned,
          shouldUseWalletBalance: options?.shouldUseWalletBalance,
        },
      );
      logInfo(
        "payment-confirmation",
        "POST response received from select-payment-method",
        {
          sessionId: newSession.id,
          status: newSession.status,
          paymentMethod: PaymentMethod.APPLE_PAY,
          shouldShowIvr: true,
          termsAccepted: options?.termsAccepted,
          documentsSigned: options?.documentsSigned,
          shouldUseWalletBalance: options?.shouldUseWalletBalance,
        },
      );

      onSessionFetched(newSession);

      setProcessing(Processing.NONE);
      return;
    }

    const {
      type: result,
      token,
      session: applePaySession,
    } = await applePay.pay(
      window,
      ctx,
      amountToPay(ctx, isWalletSelected),
      (validationUrl: string) =>
        client.validateAppleSession(
          ctx.configuration,
          validationUrl,
          ctx.session.order.currency,
          ctx.configuration.ckoHubVersion,
          ctx.session.order.countryCode,
        ),
    );

    if (result === applePay.ApplePayResultType.CANCELED) {
      logInfo("payment-confirmation", "Apple Pay was canceled");
      emitApplePayTracking("apple_pay_canceled", ctx.session);
      emitPaymentSubmissionTracking("payment_submission_failed", ctx.session, {
        payment_method_type: "apple_pay",
        wallet_selected: isWalletSelected,
        error_message: "User canceled",
      });
    } else if (result === applePay.ApplePayResultType.FAILED) {
      logInfo("payment-confirmation", "Apple Pay failed");
      emitApplePayTracking("apple_pay_failed", ctx.session);
      emitPaymentSubmissionTracking("payment_submission_failed", ctx.session, {
        payment_method_type: "apple_pay",
        wallet_selected: isWalletSelected,
        error_message: "Apple Pay failed",
      });
    } else if (result === applePay.ApplePayResultType.OK) {
      logInfo("payment-confirmation", "Apple Pay succeeded", {
        hasToken: !!token,
      });

      try {
        logInfo(
          "payment-confirmation",
          "POST request triggered to select-payment-method",
          {
            paymentMethod: PaymentMethod.APPLE_PAY,
            hasToken: !!token,
          },
        );
        const updatedSession = await client.selectPaymentMethod(
          PaymentMethod.APPLE_PAY,
          {
            token: token,
            termsAccepted: options?.termsAccepted,
            documentsSigned: options?.documentsSigned,
            shouldUseWalletBalance: options?.shouldUseWalletBalance,
          },
        );
        logInfo(
          "payment-confirmation",
          "POST response received from select-payment-method",
          {
            sessionId: updatedSession.id,
            status: updatedSession.status,
            paymentMethod: PaymentMethod.APPLE_PAY,
            hasToken: !!token,
            termsAccepted: options?.termsAccepted,
            documentsSigned: options?.documentsSigned,
            shouldUseWalletBalance: options?.shouldUseWalletBalance,
          },
        );

        // Track Apple Pay completed and payment submission completed
        emitApplePayTracking("apple_pay_completed", ctx.session);
        emitApplePayTracking("apple_pay_payment_completed", ctx.session);
        emitPaymentSubmissionTracking(
          "payment_submission_completed",
          ctx.session,
          {
            payment_method_type: "apple_pay",
            wallet_selected: isWalletSelected,
          },
        );

        onSessionFetched(updatedSession);

        logInfo(
          "payment-confirmation",
          "Sending completePayment message to Apple",
        );

        applePay.completePayment(window, applePaySession, true);

        logInfo("payment-confirmation", "completePayment sent");
      } catch (err) {
        emitApplePayTracking("apple_pay_payment_failed", ctx.session);
        emitPaymentSubmissionTracking(
          "payment_submission_failed",
          ctx.session,
          {
            payment_method_type: "apple_pay",
            wallet_selected: isWalletSelected,
            error_message: err instanceof Error ? err.message : String(err),
          },
        );

        // Attempt smart recovery for HTTP errors (e.g., 409 Conflict)
        try {
          const recoveryResult = await handleHttpError(err as ApiError, {
            refetchSession: async () => {
              const freshData = await client.getCheckoutSessionV3();
              return CksCheckoutStateSchema.parse(freshData);
            },
            currentSession: ctx.session,
            endpoint: "select-payment-method",
            httpMethod: "POST",
            requestContext: {
              paymentMethod: "APPLE_PAY",
              hasToken: !!token,
              isWalletSelected,
            },
          });

          if (recoveryResult.recovered) {
            // Check if the session is in a successful/processing state
            const isSuccessState = [
              CheckoutStatus.FINISHED,
              CheckoutStatus.SUCCEEDED,
              ...CHECKOUT_STATUS_PROCESSING_PAYMENT,
            ].includes(recoveryResult.session.status);

            logInfo(
              "payment-confirmation",
              "Recovered from Apple Pay error via session refetch",
              {
                recoveredStatus: recoveryResult.session.status,
                originalError: (err as ApiError).response?.status,
                isSuccessState,
              },
            );

            // Complete Apple Pay based on recovered state
            applePay.completePayment(window, applePaySession, isSuccessState);
            onSessionFetched(recoveryResult.session);
            setProcessing(Processing.NONE);
            return;
          }

          // Recovery failed - mark Apple Pay as failed
          logInfo(
            "payment-confirmation",
            "Sending completePayment (failure) message to Apple after failed recovery",
          );
          applePay.completePayment(window, applePaySession, false);
          onSessionFetched(recoveryResult.session);
          setProcessing(Processing.NONE);
          return;
        } catch {
          // Recovery itself failed - signal failure to Apple
          logInfo(
            "payment-confirmation",
            "Sending completePayment (failure) message to Apple after recovery error",
          );
          applePay.completePayment(window, applePaySession, false);
          setProcessing(Processing.NONE);
          throw err;
        }
      }
    }

    setProcessing(Processing.NONE);
  } catch (err) {
    // Outer catch for IVR path and other errors
    // Attempt recovery for IVR path errors
    try {
      const recoveryResult = await handleHttpError(err as ApiError, {
        refetchSession: async () => {
          const freshData = await client.getCheckoutSessionV3();
          return CksCheckoutStateSchema.parse(freshData);
        },
        currentSession: ctx.session,
        endpoint: "select-payment-method",
        httpMethod: "POST",
        requestContext: {
          paymentMethod: "APPLE_PAY",
          isIvr: ctx.session.shouldShowIvr,
        },
      });

      setProcessing(Processing.NONE);

      if (recoveryResult.recovered) {
        logInfo(
          "payment-confirmation",
          "Recovered from Apple Pay IVR error via session refetch",
          {
            recoveredStatus: recoveryResult.session.status,
            originalError: (err as ApiError).response?.status,
          },
        );
        onSessionFetched(recoveryResult.session);
        return;
      }

      onSessionFetched(recoveryResult.session);
      throw err;
    } catch {
      setProcessing(Processing.NONE);
      throw err;
    }
  }
};

const payWithWalletOnly = async (
  setProcessing: (processing: Processing) => void,
  onSessionFetched: (session: CheckoutSession) => void,
  client: CosmosClient,
  options?: {
    termsAccepted?: boolean;
    documentsSigned?: boolean;
  },
  session?: CheckoutSession,
) => {
  if (session?.id) {
    const store = newLocalStorageStore(window, session.id);
    await lib3ds.clear3dsState(store);
    lib3ds.clearSessionFlags(client.config().checkoutId);
    logInfo(
      "payment-confirmation",
      "Cleared 3DS state for wallet-only payment",
      {
        sessionId: session.id,
      },
    );
  }

  setProcessing(Processing.SUBMIT);

  try {
    logInfo(
      "payment-confirmation",
      "POST request triggered to select-payment-method",
      {
        paymentMethod: PaymentMethod.CREDIT_CARD,
        isWalletOnly: true,
      },
    );
    const newSession = await client.selectPaymentMethod(
      PaymentMethod.CREDIT_CARD,
      {
        termsAccepted: options?.termsAccepted,
        documentsSigned: options?.documentsSigned,
        shouldUseWalletBalance: true,
      },
    );
    logInfo(
      "payment-confirmation",
      "POST response received from select-payment-method",
      {
        sessionId: newSession.id,
        status: newSession.status,
        paymentMethod: PaymentMethod.CREDIT_CARD,
        termsAccepted: options?.termsAccepted,
        documentsSigned: options?.documentsSigned,
        shouldUseWalletBalance: true,
      },
    );

    // Track payment submission completed
    if (session) {
      emitPaymentSubmissionTracking("payment_submission_completed", session, {
        payment_method_type: "wallet_only",
        wallet_selected: true,
      });
    }

    onSessionFetched(newSession);

    setProcessing(Processing.NONE);
  } catch (err) {
    // Track payment submission failed
    if (session) {
      emitPaymentSubmissionTracking("payment_submission_failed", session, {
        payment_method_type: "wallet_only",
        wallet_selected: true,
        error_message: err instanceof Error ? err.message : String(err),
      });
    }

    // Attempt smart recovery for HTTP errors (e.g., 409 Conflict)
    try {
      const recoveryResult = await handleHttpError(err as ApiError, {
        refetchSession: async () => {
          const freshData = await client.getCheckoutSessionV3();
          return CksCheckoutStateSchema.parse(freshData);
        },
        currentSession: session,
        endpoint: "select-payment-method",
        httpMethod: "POST",
        requestContext: {
          paymentMethod: "CREDIT_CARD",
          isWalletOnly: true,
        },
      });

      setProcessing(Processing.NONE);

      if (recoveryResult.recovered) {
        logInfo(
          "payment-confirmation",
          "Recovered from wallet payment error via session refetch",
          {
            recoveredStatus: recoveryResult.session.status,
            originalError: (err as ApiError).response?.status,
          },
        );
        onSessionFetched(recoveryResult.session);
        return;
      }

      onSessionFetched(recoveryResult.session);
      throw err;
    } catch {
      setProcessing(Processing.NONE);
      throw err;
    }
  }
};

const cardItemName = (option: PaymentMethodOption) => {
  if (option.paymentMethodType === PaymentMethodType.APPLE) {
    return "Apple Pay";
  }

  return (
    new Map<CardScheme, string>([
      [CardScheme.MASTERCARD, "Mastercard"],
      [CardScheme.VISA, "Visa"],
      [CardScheme.AMEX, "AMEX"],
      [CardScheme.MADA, "mada"],
      [CardScheme.KNET, "KNET"],
    ]).get(option.card ? resolveScheme(option.card) : CardScheme.UNKNOWN) ??
    "Unknown"
  );
};

const selectPaymentMethod = (
  set: (method: PpsPaymentMethodDetails) => void,
  method: PaymentMethodOption,
) => {
  set({
    type:
      method.paymentMethodType === PaymentMethodType.APPLE
        ? "apple_pay"
        : "card",
    id: (method.card?.id ?? "") + "",
    name: "",
    last4: method.card?.last4 ?? "",
    icon: "",
  });
};

const autoSelectPayment = (
  paymentMethodsRef: RefObject<PaymentMethodOption[] | null>,
  selectedPaymentMethod: PpsPaymentMethodDetails | undefined,
  setSelectedPaymentMethod: (method: PpsPaymentMethodDetails) => void,
) => {
  const paymentMethods = paymentMethodsRef.current ?? [];

  if (selectedPaymentMethod || paymentMethods.length === 0) {
    return;
  }

  const applePayPaymentMethod: PaymentMethodOption | null =
    paymentMethods.find(
      (paymentMethod) =>
        paymentMethod.paymentMethodType === PaymentMethodType.APPLE,
    ) ?? null;

  selectPaymentMethod(
    setSelectedPaymentMethod,
    applePayPaymentMethod ?? paymentMethods[0],
  );
};

const amountToPay = (ctx: AppContext, isWalletSelected: boolean): number => {
  let amountToPayNow = ctx.selectedPayment().amountToPayNow;

  if (isWalletSelected) {
    const amount =
      ctx.session.paymentDetails?.cardDownPaymentAmountWithWallet?.amount ?? 0;

    if (amount === 0) {
      logError(
        "payment-confirmation",
        `Wallet was selected but could not resolve card down payment amount with wallet. Defaulting to amount without wallet: ${amountToPayNow}`,
        undefined,
        { amount, amountToPayNow, isWalletSelected },
      );
    } else {
      amountToPayNow = amount;
    }
  }

  return amountToPayNow;
};

const tokenizeCard = async (
  client: CosmosClient,
  setError: Dispatch<SetStateAction<PageError>>,
  customerId: string,
  cardData: CardData,
  session?: CheckoutSession,
): Promise<TokenizeCardResult> => {
  try {
    logInfo("payment-confirmation", "POST request triggered to tokenize-card");
    const result = await client.tokenizeCard(customerId, cardData);
    logInfo(
      "payment-confirmation",
      "POST response received from tokenize-card",
      {
        hasToken: !!result.token,
      },
    );

    return result;
  } catch (err) {
    logError(
      "payment-confirmation",
      "Tokenization failed",
      err instanceof Error ? err : new Error(String(err)),
      { error: err, customerId },
    );

    // Track tokenization failed
    if (session) {
      emitCardFormTracking("card_tokenization_failed", session, {
        tokenization_error: err instanceof Error ? err.message : String(err),
      });
    }

    setError(PageError.TOKENIZATION_FAILED);

    throw err;
  }
};
